// Geocoding service for frontend address lookup via backend API

// Import apiService for authenticated API calls
import apiService from './apiService';

class GeocodingService {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
  }

  // Round coordinates to reasonable precision for caching (about 1 meter accuracy)
  roundCoordinates(lat, lng) {
    const precision = 100000; // 5 decimal places
    return {
      lat: Math.round(lat * precision) / precision,
      lng: Math.round(lng * precision) / precision
    };
  }

  // Create cache key from coordinates
  getCacheKey(lat, lng) {
    const rounded = this.roundCoordinates(lat, lng);
    return `${rounded.lat},${rounded.lng}`;
  }

  // Get address from coordinates using backend geocoding API
  async getAddressFromCoordinates(lat, lng, apiKey = null) {
    // Note: apiKey parameter kept for backward compatibility but not used
    // Backend handles API key internally

    const cacheKey = this.getCacheKey(lat, lng);
    
    // Return cached result if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Return pending request if one exists
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Create new request
    const request = this.performGeocodingRequest(lat, lng, cacheKey);
    this.pendingRequests.set(cacheKey, request);

    return request;
  }

  async performGeocodingRequest(lat, lng, cacheKey) {
    try {
      // Call our backend API using apiService for proper authentication
      const data = await apiService.geocodeCoordinates(lat, lng);
      
      // Clean up pending request
      this.pendingRequests.delete(cacheKey);

      if (data.address) {
        // Cache the result
        this.cache.set(cacheKey, data.address);
        
        // Address lookup complete (removed console logging)
        
        return data.address;
      } else {
        // Fallback to coordinates if no address in response
        const fallback = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        this.cache.set(cacheKey, fallback);
        return fallback;
      }
    } catch (error) {
      // Clean up pending request
      this.pendingRequests.delete(cacheKey);
      
      console.error('Backend geocoding error:', error);
      const fallback = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      this.cache.set(cacheKey, fallback);
      return fallback;
    }
  }

  // Format address according to user requirements
  // - Full address with 2-letter country code if easily determinable
  // - Otherwise omit country
  formatAddress(fullAddress, addressComponents) {
    if (!addressComponents) return fullAddress;

    // Find country component
    const countryComponent = addressComponents.find(component =>
      component.types.includes('country')
    );

    if (countryComponent && countryComponent.short_name && countryComponent.short_name.length === 2) {
      // Check if address already contains the country code at the end
      if (!fullAddress.endsWith(countryComponent.short_name)) {
        // Replace full country name with code if present, otherwise append
        const addressParts = fullAddress.split(', ');
        if (addressParts.length > 1) {
          const lastPart = addressParts[addressParts.length - 1];
          // If last part is longer than 2 chars and we have a country code, replace it
          if (lastPart.length > 2 && countryComponent.long_name && 
              lastPart.toLowerCase().includes(countryComponent.long_name.toLowerCase())) {
            addressParts[addressParts.length - 1] = countryComponent.short_name;
            return addressParts.join(', ');
          }
        }
      }
    }

    return fullAddress;
  }

  // Clear cache (useful for testing or memory management)
  clearCache() {
    this.cache.clear();
  }

  // Get cache stats
  getCacheStats() {
    return {
      size: this.cache.size,
      pendingRequests: this.pendingRequests.size
    };
  }
}

// Export singleton instance
export const geocodingService = new GeocodingService();